import Decimal from 'decimal.js'
import type { BetSpecialOffer, BetSpecialOfferElement } from '../models/BetSpecialOffer'
import { PriceDetails } from '../models/PriceDetails'
import { Stake } from '../models/Stake'

export function calculateProjectedBoostReward(
  specialOffer: BetSpecialOffer,
  stake: Stake
): decimal.Decimal {
  stake = Stake.normalise(stake)

  if (checkSuperPickEligible(specialOffer, stake) && specialOffer.elements) {
    const boostElement = specialOffer.elements?.find(element => element.isBoost)
    if (boostElement) {
      const winReward = calculateLegBoostReward(
        boostElement,
        stake.Win,
        specialOffer.legTypeCode as string,
        'W'
      )
      const placeReward = calculateLegBoostReward(
        boostElement,
        stake.Place,
        specialOffer.legTypeCode as string,
        'P'
      )
      let totalReward = Decimal(winReward).plus(placeReward)

      if (boostElement.maxReward && new Decimal(boostElement.maxReward).greaterThan(0)) {
        totalReward = Decimal.min(boostElement.maxReward, totalReward)
      }

      return new Decimal(totalReward.toFixed(2, Decimal.ROUND_FLOOR))
    }
  }

  return Decimal(0)
}

export function checkSuperPickEligible(specialOffer: BetSpecialOffer, stake?: Stake): boolean {
  stake = Stake.normalise(stake)
  if (specialOffer.isExclusiveLegType) {
    return (
      (specialOffer.legTypeCode === 'W' && stake.Place.isZero()) ||
      (specialOffer.legTypeCode === 'P' && stake.Win.isZero()) ||
      (specialOffer.legTypeCode === 'WP' && (stake.Win.isZero() || stake.Place.isZero()))
    )
  } else {
    return (
      (specialOffer.legTypeCode === 'W' && (!stake.Win.isZero() || stake.Place.isZero())) ||
      (specialOffer.legTypeCode === 'P' && (stake.Win.isZero() || !stake.Place.isZero())) ||
      specialOffer.legTypeCode === 'WP'
    )
  }
}

export function calculateProjectedReward(
  specialOffer: BetSpecialOffer,
  stake: Stake,
  priceDetails: PriceDetails
): decimal.Decimal {
  stake = Stake.normalise(stake)
  priceDetails = PriceDetails.normalise(priceDetails)

  if (checkSuperPickEligible(specialOffer, stake) && specialOffer.elements) {
    const nonBoostElement = specialOffer.elements?.find(element => !element.isBoost)
    if (nonBoostElement) {
      const winReward = calculateLegReward(
        nonBoostElement,
        stake.Win,
        priceDetails.Win.DollarReturn,
        specialOffer.legTypeCode as string,
        'W'
      )
      const placeReward = calculateLegReward(
        nonBoostElement,
        stake.Place,
        priceDetails.Place.DollarReturn,
        specialOffer.legTypeCode as string,
        'P'
      )
      let totalReward = Decimal(winReward).plus(placeReward)

      if (nonBoostElement.maxReward && new Decimal(nonBoostElement.maxReward).greaterThan(0)) {
        totalReward = Decimal.min(nonBoostElement.maxReward, totalReward)
      }

      return new Decimal(totalReward.toFixed(2, Decimal.ROUND_FLOOR))
    }
  }

  return Decimal(0)
}

export function calculateLegBoostReward(
  specialOfferElement: BetSpecialOfferElement,
  individualStake: decimal.Decimal,
  offerLegTypeCode: string,
  calculationLegTypeCode: string
): decimal.Decimal {
  if (legTypeIsEligible(offerLegTypeCode, calculationLegTypeCode)) {
    const stake = validateStake(specialOfferElement, individualStake)

    const priceIncreased = specialOfferElement.priceIncrease
      ? calculationLegTypeCode === 'P'
        ? new Decimal(specialOfferElement.priceIncrease.place || 0)
        : new Decimal(specialOfferElement.priceIncrease.win || 0)
      : new Decimal(0)

    return stake.times(priceIncreased)
  }

  return new Decimal(0)
}

export function legTypeIsEligible(offerLegTypeCode: string, legTypeCode: string): boolean {
  return offerLegTypeCode === 'WP' || offerLegTypeCode === legTypeCode
}

export function validateStake(
  specialOfferElement: BetSpecialOfferElement,
  individualStake: decimal.Decimal
): decimal.Decimal {
  let stake = new Decimal(individualStake)
  const maxStake = new Decimal(specialOfferElement.maxStake || 0)
  if (maxStake.greaterThan(0)) {
    stake = Decimal.min(maxStake, stake)
  }
  return stake
}

export function calculateLegReward(
  specialOfferElement: BetSpecialOfferElement,
  individualStake: decimal.Decimal,
  dollarReturn: decimal.Decimal,
  offerLegTypeCode: string,
  calculationLegTypeCode: string
): decimal.Decimal {
  if (legTypeIsEligible(offerLegTypeCode, calculationLegTypeCode)) {
    const stake = validateStake(specialOfferElement, individualStake)

    const profit = new Decimal(dollarReturn).plus(-1.0)
    const actualProfitMultiplier = new Decimal(profit).times(
      specialOfferElement.profitMultiplier || 0
    )
    const combinedMultiplier = new Decimal(actualProfitMultiplier).plus(
      specialOfferElement.stakeMultiplier || 0
    )

    return stake.times(combinedMultiplier)
  }

  return new Decimal(0)
}
